import * as PIXI from "pixi.js";
import { OrientationType } from "../renderer/constant/RendererConstants";
import { AbstractControl } from "./AbstractControl";
import { SpriteProperties } from "./properties/SpriteProperties";
/**
 * TitanSprite.ts
 *
 * A PIXI sprite that can responsively switch textures.
 */
export class TitanSprite extends AbstractControl {
    /**
     * Wrapper for a constructor call.
     * @param parent - What to attach the sprite as a child of.
     * @param name - PIXI name used for testing and development.
     * @param properties - Landscape properties.
     * @param portraitProperties - Portrait properties.
     * @returns The new {@link TitanSprite} instance.
     */
    static createSprite(parent, name, properties, portraitProperties) {
        let sprite;
        if (properties) {
            sprite = new TitanSprite(properties, portraitProperties);
            if (name) {
                sprite.name = name;
            }
            if (sprite && parent) {
                parent.addChild(sprite);
            }
        }
        return sprite;
    }
    /**
     * @param forceOrientation -  {@link OrientationType}
     * @returns The internal {@link PIXI.Sprite}.
     */
    getPixiSprite(forceOrientation) {
        const orientation = forceOrientation || this.renderer.getOrientation();
        if (this.portraitSprite && orientation === OrientationType.PORTRAIT) {
            return this.portraitSprite;
        }
        return this.landscapeSprite;
    }
    /**
     * @returns The last part of a sprite's file name.
     */
    getCurrentId() {
        return this.uiProperties.id;
    }
    constructor(properties, portraitProperties) {
        super(properties, portraitProperties);
        this.run();
    }
    createProperties(landscapeProperties, portraitProperties) {
        super.createProperties(landscapeProperties, portraitProperties);
        this.landscapeProperties = landscapeProperties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    create() {
        super.create();
        this.createSprites();
    }
    destroy() {
        super.destroy();
        this.landscapeSprite = null;
        this.portraitSprite = null;
    }
    setAnchor(x, y) {
        const anchor = new PIXI.Point(x, typeof y === "number" ? y : x);
        this.landscapeSprite.anchor.set(anchor.x, anchor.y);
        if (this.portraitSprite) {
            this.portraitSprite.anchor.set(anchor.x, anchor.y);
        }
    }
    /**
     * @param sprite - {@link TitanSprite} to be saved as landscape PIXI sprite's mask.
     */
    setMask(sprite) {
        this.landscapeSprite.mask = sprite;
    }
    stripPositionAndScale(properties) {
        return new SpriteProperties(properties.id, properties.spriteSheet, new PIXI.Point(0, 0), new PIXI.Point(1, 1), properties.anchor);
    }
    createSprites() {
        this.landscapeSprite = this.assetManager.getSprite(this.stripPositionAndScale(this.landscapeProperties), OrientationType.LANDSCAPE);
        this.landscapeSprite.renderable =
            this.renderer.isLandscape() || !this.hasPortraitProperties;
        this.addChild(this.landscapeSprite);
        if (this.portraitProperties instanceof SpriteProperties) {
            this.responsive = true;
            this.portraitSprite = this.assetManager.getSprite(this.stripPositionAndScale(this.portraitProperties), OrientationType.PORTRAIT);
            this.portraitSprite.renderable = this.renderer.isPortrait();
            this.addChild(this.portraitSprite);
        }
    }
    handleRotate(event) {
        const data = event?.getCurrentTarget() || {};
        this.uiProperties = (this.getCurrentProperties(data.forceOrientation));
        if (this.responsive) {
            const isLandscape = this.renderer.isLandscape(data.forceOrientation);
            this.landscapeSprite.renderable = isLandscape;
            if (this.portraitSprite) {
                this.portraitSprite.renderable = !isLandscape;
            }
        }
        super.handleRotate(event);
    }
}
//# sourceMappingURL=TitanSprite.js.map